import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { catchError, concatMap, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ApplicationService } from '@state/application';
import { Entity } from '@state/entities';
import { handleResolverError$ } from '@helpers/error';
import { ID } from '@state/base';
import { MembershipEntityService, MembershipEntityRepository } from '@state/membership-entity';
import { MembershipService } from '@state/membership';

@Injectable({
  providedIn: 'root'
})
export class MembershipEntityResolver {
  private applicationService = inject(ApplicationService);
  private membershipEntityService = inject(MembershipEntityService);
  private membershipEntityRepository = inject(MembershipEntityRepository);
  private membershipService = inject(MembershipService);
  private router = inject(Router);

  resolve(route: ActivatedRouteSnapshot): Observable<Entity> {
    let activeMembershipEntity = this.membershipEntityRepository.getMembershipEntity();
    let membershipEntityId: ID | undefined = route.params.membership_entity_id;
    let providerEntityId: ID = route.params.provider_entity_id;

    // Reset entity store when not scoped under /e/:membership_entity_id
    if (!membershipEntityId) this.membershipEntityRepository.reset();

    // Skip the entity load entirely if the requested id is already the active entity
    if (membershipEntityId && membershipEntityId === activeMembershipEntity?.id) {
      return of(activeMembershipEntity);
    }

    let entityLoad$ = membershipEntityId
      ? this.applicationService.load$(
          this.membershipEntityService
            .getAndStoreMembershipEntity$(membershipEntityId, providerEntityId)
            .pipe(catchError((error: HttpErrorResponse) => handleResolverError$(error, this.router))),
          'membership-entity'
        )
      : of(undefined);

    let membershipLoad$ = membershipEntityId
      ? this.membershipService.getAndStoreMembership$(membershipEntityId, providerEntityId)
      : this.membershipService.getAndStoreSelfMembership$(providerEntityId);

    // The membership needs to be loaded after we have the membership entity so we have the correct IDs.
    // Unfortunately at the time there was no mechanism to have resolvers perform in order (and I'm still
    // not sure that is feasible) and so for the time being we concat onto this resolver to load the
    // membership itself, and then return the membership entity once both are done.
    return entityLoad$.pipe(
      concatMap((membershipEntity) =>
        this.applicationService.load$(membershipLoad$, 'membership').pipe(
          map(() => membershipEntity),
          catchError(() => of(membershipEntity))
        )
      )
    );
  }
}
